import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import type { Section } from "../sections";

/**
 * The scope's sections, as links — the sidebar's contents, and the drawer's (`Frame`).
 *
 * `NavLink` marks the current one with `aria-current="page"` as well as the filled pill, so
 * the state is not carried by colour alone.
 *
 * The heading above them is the wireframes' — *"The family's pages"* — and it earns its line
 * because the person scope's nav sits in the same place with the same shape, and a label is
 * what tells you which of the two you are in without reading the items.
 */
export function NavItems({
  base,
  heading,
  sections,
}: {
  base: string;
  heading: string;
  sections: readonly Section[];
}) {
  return (
    <>
      <p className="nav__heading">{heading}</p>
      <ul className="nav">
        {sections.map((section) => (
          <li key={section.path}>
            <NavLink
              className={({ isActive }) => `nav__item${isActive ? " nav__item--current" : ""}`}
              to={`${base}/${section.path}`}
            >
              <section.Icon />
              <span className="nav__item-label">{section.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * The way *out* of this scope.
 *
 * #293's wireframes make the sidebar the thing that switches scope — family or you, never
 * both — so each scope carries one route to the other. On the family side that is the account
 * chip in the header; on the person side it is a route back to the family, at the top of its
 * nav, which is what `PersonScope` passes here.
 */
export function NavExit({ children }: { children: ReactNode }) {
  return <div className="nav__exit">{children}</div>;
}

/**
 * The way out of the panel and into the other half of Boone.
 *
 * **The panel never mentioned the assistant, and that was the gap.** Someone who arrived here
 * from an invitation link could read every screen and never learn that Boone runs inside Claude
 * or ChatGPT — and the panel is the *quiet* half: nothing here needs doing, while everything a
 * family actually says happens in the conversation. A control panel that never names the thing
 * it controls leaves its owner looking at a record with no idea what writes to it.
 *
 * Two links and no more. *How Boone works* is the explanation — where it lives and what happens
 * to something you say — and *Connect an assistant* is the one action this panel cannot do
 * itself, because connecting happens inside the host app.
 *
 * **They sit at the foot of the sidebar, not among the sections.** A section is a place inside
 * the panel; these leave it. `margin-top: auto` puts them below whatever the scope's own list
 * is, so the shape is the same in both scopes without either one knowing how tall the other is.
 */
export function NavOffsite() {
  return (
    <div className="nav__offsite">
      <p className="nav__heading">Boone elsewhere</p>
      <ul className="nav">
        <li>
          <a className="nav__offsite-item" href="https://iamboone.app/how-it-works">
            How Boone works
          </a>
        </li>
        <li>
          <a className="nav__offsite-item" href="https://iamboone.app/connect">
            Connect an assistant
          </a>
        </li>
      </ul>
    </div>
  );
}
